import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { LOBBY_SECTION_NAME } from '@repo/common';

import { SearchButton } from 'features/navigation';

import { useOpenType } from '../../core/providers/OpenTypeContext';
import { externalCommunication } from '../../core/services/communication/ExternalCommunication';
import { clickCounter } from '../../core/services/EventTracker';
import { useAppStore, useViewMode, setSelectedSection, setViewMode } from '../../store/app/appStore';
import { MULTI_VIEW_MODE } from '../../constants';
import Image from '../../shared/components/Image';
import TransWithFallback from '../../shared/components/Translations/TransWrapper';
import { CAMPAIGN_TYPE_TO_SECTION, SECTION_TO_CAMPAIGN_TYPE, useCampaignSectionGate } from 'features/campaigns';

import styles from './InGameHeader.module.css';

const InGameHeader: React.FC = () => {
  const { t } = useTranslation();
  const { openType } = useOpenType();

  const campaignOnly = useAppStore((state) => state.settings.campaignOnly);
  const setIdle = useAppStore((state) => state.setIdle);
  const viewMode = useViewMode();
  const selectedSection = useAppStore((state) => state.selectedSection);

  // Any campaign-only type (tournaments, prize pop, rebate, etc.) shares the same
  // header behavior, so derive it from the section gate. A type whose campaign is
  // disabled is not treated as campaign-only and falls back to the normal lobby header.
  const { isCampaignEnabled } = useCampaignSectionGate();

  const campaignSection = campaignOnly?.type ? CAMPAIGN_TYPE_TO_SECTION[campaignOnly.type] : undefined;
  const activeCampaignSection =
    campaignSection && isCampaignEnabled(campaignSection) === true ? campaignSection : undefined;

  // CSS-friendly campaign key, e.g. SECTION_NAMES.PRIZE_POP -> 'prize-pop'
  const dataCampaignsValue = useMemo(
    () => (activeCampaignSection ? activeCampaignSection.toLowerCase().replace('_', '-') : 'none'),
    [activeCampaignSection],
  );


  // Only render in ingame mode
  if (openType !== 'ingame') {
    return null;
  }

  let headerKey: string;
  let headerFallback: string;

  if (SECTION_TO_CAMPAIGN_TYPE[selectedSection] && viewMode !== MULTI_VIEW_MODE) {
    headerKey = `sections.names.${selectedSection}`;
    headerFallback = selectedSection.replace('_', ' ');
  } else {
    headerKey = `tabs.names.${LOBBY_SECTION_NAME}_TAB`;
    headerFallback = LOBBY_SECTION_NAME;
  }

  const headerTitle = <TransWithFallback
    i18nKey={headerKey}
    components={{ pnl: <br />, nl: <br /> }}
    customFallbackTranslation={headerFallback}
  />

  const handleClose = () => {
    // Track with consistent naming: click_exit_ingame
    clickCounter.incrementClick('click_exit_ingame');

    setIdle(true);

    // Send display event to hide the lobby
    externalCommunication.sendMessage('display', false);

    // The client hides the iframe without echoing DISPLAY back. In campaign-only mode
    // hold off rendering until the next SETTINGS (or the show-grace timeout) so a stale
    // campaign view can't flash when the client re-shows the lobby for another widget.
    if (campaignOnly?.type) {
      useAppStore.getState().setAwaitingSettingsAfterHide(true);
    }

    // Close search overlay if open so navigation is visible when lobby reopens
    const { isSearchActive, setSearchActive, setSearchQuery } = useAppStore.getState();
    if (isSearchActive) {
      setSearchActive(false);
      setSearchQuery('');
    }

    // Only reset to LOBBY if we're NOT in campaign-only mode (tournaments/prizePop/rebate/etc.).
    // Campaign-only views stay on the last opened campaign, so reopening the
    // in-game lobby (display: true, no new SETTINGS) shows it again instead of
    // jumping to the multi-view lobby.
    if (!activeCampaignSection) {
      // Set Timeout to prevent flickering
      setTimeout(() => {
        setSelectedSection(LOBBY_SECTION_NAME);
        setViewMode(MULTI_VIEW_MODE);
      }, 500);
    }
  };

  return (
    <div className={styles.header} data-campaigns={dataCampaignsValue}>
      {!activeCampaignSection && <SearchButton className={styles.searchButton} />}
      <h1 className={styles.title}>
        {headerTitle}
      </h1>
      <button className={styles.closeButton} onClick={handleClose} aria-label={t('close', 'Close')}>
        <Image src={`/ui/popupCloseRedThin`} manualAffix=".svg" className="egtd-popup-close-icon-button" alt="" />
      </button>
    </div>
  );
};

export default InGameHeader;
